import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { ContextBar } from "../components/ContextBar";
import { TelaCarregando } from "../components/ProtectedRoute";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { api, erroMsg } from "../lib/api";
import { brl, parsePctInput, pct, toPctInput } from "../lib/format";
import { useCart } from "../context/CartContext";
import { useMeta } from "../context/MetaContext";
import { ProductImage } from "../components/ProductImage";
import { AlertTriangle, ArrowLeft, Minus, Plus, ShoppingCart } from "lucide-react";

export default function ProdutoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { uf, enquadramento, adicionar } = useCart();
  const { ufNome, enqNome } = useMeta();

  const [produto, setProduto] = useState(null);
  const [comparativo, setComparativo] = useState([]);
  const [mapa, setMapa] = useState([]);
  const [qtd, setQtd] = useState(1);
  const [descTexto, setDescTexto] = useState("0");
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const descFracao = parsePctInput(descTexto);
  const maxDesc = Number(produto?.desconto_maximo || 0);
  const acimaDoMaximo = descFracao > maxDesc + 1e-9;
  const descAplicado = Math.min(descFracao, maxDesc);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [p, c, m] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/products/${id}/comparativo`, { params: { uf, desconto: 0 } }),
        api.get(`/products/${id}/mapa-estados`, { params: { enquadramento, desconto: 0 } }),
      ]);
      setProduto(p.data);
      setComparativo(c.data.resultados);
      setMapa(m.data.linhas);
      setErro("");
    } catch (e) {
      setErro(erroMsg(e, "Produto não encontrado."));
    } finally {
      setCarregando(false);
    }
  }, [id, uf, enquadramento]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (carregando && !produto) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <TelaCarregando texto="Carregando produto…" />
      </div>
    );
  }

  if (erro || !produto) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center">
          <p className="text-sm text-destructive">{erro || "Produto não encontrado."}</p>
          <Button className="mt-4" onClick={() => navigate("/consulta")}>
            Voltar para a consulta
          </Button>
        </div>
      </div>
    );
  }

  const atual = comparativo.find((c) => c.enquadramento === enquadramento);
  const aliq = Number(atual?.calculo?.aliquota_st || 0);

  // simulação local com o desconto digitado (mesma fórmula do backend)
  const base = Number(produto.valor_unitario) * (1 - descAplicado);
  const ipiVal = base * Number(produto.ipi || 0);
  const stVal = (base + ipiVal) * aliq;
  const totalUnit = Math.round((base + ipiVal + stVal) * 100) / 100;

  const linhas = [
    { label: "Valor de tabela", valor: brl(produto.valor_unitario) },
    {
      label: `Desconto simulado (${pct(descAplicado)})`,
      valor: `− ${brl(Number(produto.valor_unitario) - base)}`,
      cor: "text-emerald-700",
    },
    { label: "Valor líquido", valor: brl(base), forte: true },
    { label: `IPI (${pct(produto.ipi)})`, valor: `+ ${brl(ipiVal)}` },
    {
      label: `ICMS-ST / DIFAL (${pct(aliq)})`,
      valor: aliq ? `+ ${brl(stVal)}` : "sem incidência",
    },
    { label: "Preço unitário final", valor: brl(totalUnit), destaque: true },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <AppHeader />

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6">
          <Link
            to="/consulta"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 transition-colors hover:text-primary"
            data-testid="produto-voltar"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar para a consulta
          </Link>
          <div className="mt-2 flex gap-3.5">
            <ProductImage
              fotoPath={produto.foto_path}
              alt={produto.descricao_produto}
              className="h-20 w-20 rounded-xl border border-slate-200 sm:h-24 sm:w-24"
              iconClassName="h-7 w-7"
            />
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-[22px] font-bold leading-tight tracking-tight text-slate-900 sm:text-2xl">
                {produto.descricao_produto}
              </h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="font-mono-num text-[11px]">
                  {produto.codigo}
                </Badge>
                <Badge variant="outline" className="font-mono-num text-[11px]">
                  NCM {produto.ncm}
                </Badge>
                <Badge
                  variant="secondary"
                  className={`text-[10px] uppercase ${
                    produto.origem === "IMPORTAÇÃO"
                      ? "bg-amber-100 text-amber-900"
                      : "bg-[#EEF2F9] text-[#1B315E]"
                  }`}
                >
                  {produto.origem === "IMPORTAÇÃO" ? "Importação" : "Fabricação"}
                </Badge>
                <span className="text-[12px] text-slate-500">Unidade: {produto.unidade}</span>
                <span className="text-[12px] text-slate-500">
                  Desconto máximo: <b>{pct(produto.desconto_maximo)}</b>
                </span>
              </div>
              {produto.descricao_tecnica && (
                <p className="mt-2 max-w-3xl text-[13px] leading-relaxed text-slate-600">
                  {produto.descricao_tecnica}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-3 py-5 sm:px-6">
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
          <ContextBar />
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <Tabs defaultValue="calculo">
              <TabsList className="mb-4" data-testid="produto-comparar-tabs">
                <TabsTrigger value="calculo" data-testid="tab-calculo">
                  Cálculo
                </TabsTrigger>
                <TabsTrigger value="enquadramentos" data-testid="tab-enquadramentos">
                  Comparar enquadramentos
                </TabsTrigger>
                <TabsTrigger value="estados" data-testid="tab-estados">
                  Todos os estados
                </TabsTrigger>
              </TabsList>

              <TabsContent value="calculo">
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm" data-testid="produto-breakdown-table">
                    <tbody>
                      {linhas.map((l) => (
                        <tr
                          key={l.label}
                          className={`border-b border-slate-100 last:border-0 ${
                            l.destaque ? "bg-primary/[0.07]" : ""
                          }`}
                        >
                          <td className="px-3 py-2.5 text-slate-600">{l.label}</td>
                          <td
                            className={`num px-3 py-2.5 text-right ${
                              l.destaque
                                ? "font-display text-[19px] font-bold text-primary"
                                : l.forte
                                ? "font-semibold text-slate-900"
                                : "text-slate-800"
                            } ${l.cor || ""}`}
                            data-testid={l.destaque ? "produto-preco-total" : undefined}
                          >
                            {l.valor}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-[12px] leading-relaxed text-slate-500">
                  Cálculo para <b>{ufNome(uf)}</b> com enquadramento{" "}
                  <b>{enqNome(enquadramento)}</b>. O ICMS-ST/DIFAL incide sobre o valor líquido
                  somado ao IPI. Use o campo ao lado para simular um desconto antes de adicionar ao
                  orçamento.
                </p>
                {produto.nota_st && (
                  <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-[11.5px] text-slate-600">
                    {produto.nota_st}
                  </p>
                )}
              </TabsContent>

              <TabsContent value="enquadramentos">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Enquadramento</TableHead>
                        <TableHead className="text-right">Alíq. ST</TableHead>
                        <TableHead className="text-right">ST R$</TableHead>
                        <TableHead className="text-right">Preço de tabela</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparativo.map((r) => (
                        <TableRow
                          key={r.enquadramento}
                          className={r.enquadramento === enquadramento ? "bg-primary/[0.06]" : ""}
                          data-testid={`comp-row-${r.enquadramento}`}
                        >
                          <TableCell className="font-medium">{r.nome}</TableCell>
                          <TableCell className="num text-right">
                            {r.calculo.aliquota_st ? pct(r.calculo.aliquota_st) : "—"}
                          </TableCell>
                          <TableCell className="num text-right">{brl(r.calculo.st_valor)}</TableCell>
                          <TableCell className="num text-right font-semibold">
                            {brl(r.calculo.total_unitario)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="estados">
                <div className="max-h-[460px] overflow-auto rounded-lg border border-slate-200">
                  <Table className="sticky-head">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Alíq. ST</TableHead>
                        <TableHead className="text-right">Preço de tabela</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mapa.map((l) => (
                        <TableRow
                          key={l.uf}
                          className={l.uf === uf ? "bg-primary/[0.06]" : ""}
                          data-testid={`mapa-row-${l.uf}`}
                        >
                          <TableCell className="font-medium">
                            {l.uf} — {l.nome}
                          </TableCell>
                          <TableCell className="num text-right">
                            {l.calculo.aliquota_st ? pct(l.calculo.aliquota_st) : "—"}
                          </TableCell>
                          <TableCell className="num text-right font-semibold">
                            {brl(l.calculo.total_unitario)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-2 text-[12px] text-slate-500">
                  Enquadramento considerado: <b>{enqNome(enquadramento)}</b>
                </p>
              </TabsContent>
            </Tabs>
          </div>

          {/* card de adição */}
          <div className="h-fit rounded-xl border border-slate-200 bg-white p-4 lg:sticky lg:top-24">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
              Preço final unitário
            </div>
            <div className="num font-display text-[32px] font-bold leading-tight text-slate-900">
              {brl(totalUnit)}
            </div>
            <div className="text-[12px] text-slate-500">por {produto.unidade}</div>

            <div className="mt-4 space-y-1.5">
              <Label htmlFor="desc">Desconto para este item (%)</Label>
              <div className="relative">
                <Input
                  id="desc"
                  inputMode="decimal"
                  value={descTexto}
                  onChange={(e) => setDescTexto(e.target.value)}
                  className={`h-11 pr-8 text-[15px] font-semibold num ${
                    acimaDoMaximo ? "border-destructive focus-visible:ring-destructive" : ""
                  }`}
                  data-testid="produto-desconto-input"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">
                  %
                </span>
              </div>
              {acimaDoMaximo ? (
                <div
                  className="flex items-start gap-1.5 text-[11.5px] text-destructive"
                  data-testid="produto-desconto-erro"
                >
                  <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                  <span>
                    Acima do máximo permitido ({pct(maxDesc)}). Será aplicado {pct(maxDesc)}.
                  </span>
                </div>
              ) : (
                <div className="text-[11.5px] text-slate-500">
                  Máximo permitido: <b>{pct(maxDesc)}</b>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-1.5">
              <Label htmlFor="qtd">Quantidade</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 shrink-0"
                  onClick={() => setQtd((q) => Math.max(1, Number(q) - 1))}
                  data-testid="produto-qtd-menos"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="qtd"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={qtd}
                  onChange={(e) => setQtd(e.target.value)}
                  className="h-11 text-center text-[15px] font-semibold num"
                  data-testid="produto-quantidade-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 shrink-0"
                  onClick={() => setQtd((q) => Number(q) + 1)}
                  data-testid="produto-qtd-mais"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2.5">
              <div className="flex items-center justify-between text-[12px] text-slate-600">
                <span>Total do item</span>
                <span className="num font-display text-[17px] font-bold text-slate-900">
                  {brl(totalUnit * (Number(qtd) || 0))}
                </span>
              </div>
            </div>

            <Button
              className="mt-4 h-11 w-full"
              onClick={() => {
                adicionar(produto, Number(qtd) || 1, descAplicado);
                toast.success("Adicionado ao orçamento", {
                  description: `${qtd} × ${produto.descricao_produto}${
                    descAplicado > 0 ? ` • desconto ${toPctInput(descAplicado)}%` : ""
                  }`,
                  action: { label: "Ver orçamento", onClick: () => navigate("/orcamento") },
                });
              }}
              data-testid="produto-adicionar-orcamento-button"
            >
              <ShoppingCart className="mr-2 h-4 w-4" /> Adicionar ao orçamento
            </Button>

            {produto.observacao && (
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900">
                {produto.observacao}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
